import { FieldViewModel } from "../fields"
import { LayerTypeId } from "../generated/enums"
import { LocaleString } from "../types"
import { isArrayEmpty } from "../utils/array"
import { isArray } from "../utils/utils"
import { Datalayer } from "./datalayer"
import { DatalayerZone } from "./datalayerZone"
import { IDataLayer, IDatalayerContext, IDatalayerKeyItem, IDatalayersMap, IDatalayerViewerFactory, ILayerViewModel, ILayerViewModelOptions, LayerEditMode } from "./types"
import { getLayerDescription } from "./utils"
import { setupVariants } from "./variants"
import { ol_extent, ol_Feature, ol_Overlay, ol_geom } from "@omnia/exports/openlayers";

export class LayerViewModel implements ILayerViewModel {

    public readonly cropping = getCropping(this.field, this.datalayer.CroppingId)

    private _mapLayer: Datalayer
    private _init: boolean = false

    private _loading: PromiseLike<any>

    private _viewers: IDatalayerViewerFactory[]

    private _visible: boolean
    private _selectable: boolean = false
    private _opacity: number = 100
    private _labels: boolean = true
    private _labelsVerticalOffset: number
    private _editMode: LayerEditMode
    private _viewer: IDatalayerViewerFactory
    private _transform: ((geom: ol_geom.Geometry) => void)
    private _showTooltips: boolean = true
    private _layerLabel: string

    private _watchers: ((l: LayerViewModel) => void)[]

    private _variants: LayerViewModel[]

    constructor(
        protected _ctxt: IDatalayerContext,
        private readonly map: IDatalayersMap,
        public readonly datalayer: IDataLayer,
        public readonly field: FieldViewModel = null,
        private readonly _options: ILayerViewModelOptions = {},
    ) {

        this._selectable = !!this._options.selectable;

        this._viewers = _ctxt.viewManager.getViewers(datalayer.TypeId);

        this._variants = isArrayEmpty(datalayer.Children)
            ? undefined
            : datalayer.Children.map(c => new LayerViewModel(_ctxt, map, c, field, _options))

        if (isArray(this._variants)) {
            setupVariants(this._variants, datalayer.Type)
        }

    }

    get context(): IDatalayerContext {
        return this._ctxt;
    }

    get datalayerId(): number {
        return this.datalayer.DataLayerId;
    }

    get lastUpdated() {
        return this.datalayer.UpdatedOn || this.datalayer.CreatedOn
    }

    get mapLayer(): Datalayer {
        return this._mapLayer;
    }

    get type() {
        return this.datalayer.Type
    }

    get typeId(): LayerTypeId {
        return this.datalayer.TypeId
    }

    get fieldId(): number {
        return this.datalayer.FieldId;
    }

    get fieldName() {
        return this.field ? this.field.fieldName : null
    }

    get holdingId(): number {
        return this.datalayer.HoldingId;
    }

    get croppingId(): number {
        return this.datalayer.CroppingId;
    }

    getVariants() {
        return this._variants ? this._variants.slice() : undefined
    }

    hasVariants() {
        return !!this._variants
    }

    isVariant() {
        return !!this.datalayer.ParentId
    }

    get variantDescription() {

        if (this.isVariant()) {

            switch (this.datalayer.TerraMap) {
                case 2: return "QGIS"
                case 3: return "Dynamic scale" as LocaleString
            }
        }

    }

    get year() {
        return this.cropping
            ? this.cropping.yearId
            : this.datalayer.FirstYearId
    }

    get date() {
        return this.datalayer.Date
    }

    get crop() {


        if (this.isCroppingLayer && this.cropping) {
            return this.cropping.crop
        }

        return this.datalayer.Crop

    }

    get isCroppingLayer(): boolean {
        return !!this.croppingId;
    }

    get active(): boolean {
        return !this.datalayer.Inactive;
    }

    get visible(): boolean {
        return this._visible;
    }

    set visible(value: boolean) {
        this.setVisible(value)
    }

    get alwaysVisible(): boolean {
        return this._mapLayer.alwaysVisible;
    }

    set alwaysVisible(value: boolean) {
        this._mapLayer.alwaysVisible = value;
    }

    private _loadingOverlay = false

    public setVisible(value: boolean): PromiseLike<any> {

        this._visible = value

        if (this.field) {
            value = value && this.field.visible
        }

        if (!value) {

            this.removeMapLayer()

            if (this._variants) {
                this._variants.forEach(v => v.visible = false)
            }

            return null

        }

        const { zonesLoaded: loaded, _loadingOverlay: overlay } = this

        const p = this.showMapLayer()

        if (p && (!loaded) && (!overlay) && this._options?.showOverlayOnLoad) {

            const { map, fieldId, croppingId } = this

            const feature = croppingId ? map.croppingLayer.findCropping(croppingId) : map.fieldsLayer.findField(fieldId)

            if (feature) {

                const geometry = feature.getGeometry()

                const extent = geometry.getExtent()
                const position = ol_extent.getCenter(extent)

                const olmap = map.getMap()


                const updateFontSize = (element: HTMLElement) => {

                    const px1 = olmap.getPixelFromCoordinate(extent.slice(0, 2))
                    const px2 = olmap.getPixelFromCoordinate(extent.slice(2))

                    const size = px1 && px2
                        ? Math.min(40, Math.max(16, Math.min(px2[0] - px1[0], px1[1] - px2[1]) / 2))
                        : 20

                    element.style.fontSize = size + "px"
                    element.style.lineHeight = size + "px"

                }

                const element = document.createElement("div")
                element.className = "oa-map-loading-overlay"
                element.innerHTML = "<i class='fas fa-circle-notch animate-spin'></i>"

                const iconElement = element.firstChild as HTMLElement

                const overlay = new ol_Overlay({
                    position,
                    positioning: "center-center",
                    element
                })

                updateFontSize(iconElement)

                this._loadingOverlay = true

                olmap.addOverlay(overlay)

                return p.then(() => {
                    olmap.removeOverlay(overlay)
                    this._loadingOverlay = false
                })

            }

        }

        return p

    }

    public refreshVisibility() {
        this.setVisible(this._visible)
    }


    get opacity(): number {
        return this._opacity;
    }

    set opacity(value: number) {
        this._opacity = value;
        if (this._mapLayer) this._mapLayer.opacity = value;
        this.map.requestStackOrderUpdate(100)
    }

    get selectable(): boolean {
        return this._selectable;
    }

    set selectable(value: boolean) {
        this._selectable = value;
        if (this._mapLayer) this._mapLayer.selectable = value;
    }

    get showLabels(): boolean {
        return this._labels;
    }

    set showLabels(value: boolean) {
        this._labels = value;
        if (this._mapLayer) this._mapLayer.showLabels = value;
    }

    get showTooltips(): boolean {
        return this._showTooltips;
    }

    set showTooltips(value: boolean) {
        this._showTooltips = value;
        if (this._mapLayer) this._mapLayer.showTooltips = value;
    }

    get editMode(): LayerEditMode {
        return this._editMode;
    }

    set editMode(value: LayerEditMode) {
        this._editMode = value;
        if (this._mapLayer) this._mapLayer.editMode = value;
    }

    checkTypeId(id: LayerTypeId) {
        return this.datalayer && (this.datalayer.TypeId == id)
    }

    get zonesLoaded(): boolean {
        return this.datalayer && (!!this.datalayer.Zones)
    }

    public ensureLayerLoaded(show: boolean): PromiseLike<any> {

        if (this.datalayer.Zones) {
            return null
        }

        this._loading = this._loading || this._ctxt.loader.loadLayerZones([this.datalayer], { buffer: true, blockUI: false })

        return this._loading.then(() => {

            this._loading = undefined

            if (show) {
                this.showLoadedMapLayer()
            }
            else {
                this.ensureInit()
            }

        })

    }

    public ensureInit() {

        if (this._init) {
            return
        }

        this.refreshOtherLayers()

        if (isArray(this._watchers)) {
            this._watchers.forEach(fn => fn(this))
        }

        this._watchers = undefined
        this._init = true

    }

    /**
     * Enqueues @fn for execution once the layer has been fully loaded
     * @fn is executed immediatly if the layer has already been loaded
     **/
    public notifyOnLoad(fn: (l: LayerViewModel) => void) {

        if (this.zonesLoaded) {
            fn(this)
        }
        else {
            this._watchers = (this._watchers || []).concat([fn])
        }

    }

    private refreshOtherLayers() {

        if (!this.type.DynamicNumericValueRange) {
            return;
        }

        let layers = this._ctxt.getAllLayers ? this._ctxt.getAllLayers() : null;

        if (layers) {

            layers
                .filter(l => l.typeId == this.typeId)
                .forEach(l => l.refresh())

        }

    }

    private showMapLayer() {

        if (this.datalayer.Zones) {
            this.showLoadedMapLayer()
            return null
        }

        return this.ensureLayerLoaded(true)

    }

    private showLoadedMapLayer() {

        const { _transform: transform, _options: { layersPopup, addToMap } } = this

        const layer = this.getMapLayer()

        if (transform) {
            layer.setTransform(transform)
        }

        if (addToMap !== false) {
            this.map.addLayer(layer, true, true)
        }

        layer.isVisible = this._visible
        layer.selectable = this._selectable
        layer.opacity = this._opacity
        layer.showLabels = this._labels
        layer.showTooltips = this._showTooltips
        layer.labelsVerticalOffset = this._labelsVerticalOffset
        layer.editMode = this._editMode

        if (layersPopup !== undefined) {
            layer.layersPopup = layersPopup
        }

        this.setLayerLabel(this._layerLabel)

        if (this._viewer) {
            layer.setViewer(this._viewer)
        }

        this.ensureInit()

    }


    getMapLayer(create: boolean = true) {

        if (create && (!this._mapLayer)) {
            this._mapLayer = this.createMapLayerInstance();
        }

        return this._mapLayer
    }

    getExtent() {
        const l = this.getMapLayer()
        return l && l.getExtent()
    }

    protected createMapLayerInstance() {

        if (this._options.layerInstance) {
            return <Datalayer>(this._options.layerInstance)
        }

        return <Datalayer>(this._ctxt.createMapLayerInstance(this.datalayer))

    }

    private removeMapLayer() {

        if (this._mapLayer) {
            this._mapLayer.isVisible = false
            this.map.removeLayer(this._mapLayer)
        }

    }

    public dispose() {
        this.removeMapLayer();
        this._mapLayer = null;
    }

    get description(): string {
        return this.getLayerDescription()
    }

    getLayerDescription() {

        const d = getLayerDescription(this.datalayer, this.cropping)

        return this.isVariant()
            ? d + " (detailed)"
            : d

    }

    get valuesKey(): IDatalayerKeyItem[] {
        if (this._mapLayer) {
            return this._mapLayer.getCurrentKey();
        }
    }

    hasMultipleViewers(): boolean {
        return this._viewers && (this._viewers.length > 1);
    }

    getCurrentViewerName(): string {
        let v = this.getViewer();
        if (v) return v.name;
    }

    getViewer() {
        return this._viewer || this._ctxt.viewManager.getCurrentViewer(this.typeId)
    }

    setViewer(viewer: IDatalayerViewerFactory) {
        this._viewer = viewer
        if (this._mapLayer) {
            this._mapLayer.setViewer(viewer)
        }
    }

    getSupportedViewers() {
        return this._ctxt.viewManager.getViewers(this.typeId)
    }

    refresh() {
        if (this._mapLayer) this._mapLayer.refresh()
    }

    refreshViewer() {
        if (this._mapLayer) this._mapLayer.refreshViewer()
    }

    canShowPoints() {
        return this._mapLayer?.canShowPoints()
    }

    get showPoints(): boolean {
        const { _mapLayer: l } = this
        return l?.showPoints()
    }

    set showPoints(value: boolean) {
        this._mapLayer.showPoints(value, this.map)
    }

    get labelsVerticalOffset(): number {
        return this._labelsVerticalOffset;
    }

    set labelsVerticalOffset(value: number) {
        this._labelsVerticalOffset = value;
        if (this.mapLayer) {
            this.mapLayer.labelsVerticalOffset = value;
            this.mapLayer.refresh();
        }

    }

    public setTransform(transform: ((geom: ol_geom.Geometry) => void)) {
        this._transform = transform;
        if (this._mapLayer) {
            this._mapLayer.setTransform(transform);
        }
    }

    public getZone(zoneId: number): DatalayerZone {

        if (this.mapLayer && this.mapLayer.zones) {
            return this.mapLayer.zones.find(z => z.id == zoneId)
        }
    }

    public setLayerLabel(label: string) {

        this._layerLabel = label

        const l = this.mapLayer

        if (l) {
            l.setLayerLabel(label)
        }

    }

    findFeatures(predicate: (feature: ol_Feature, data: any) => boolean) {
        return this._mapLayer.findFeatures(predicate)
    }

}

function getCropping(field: FieldViewModel, id: number) {
    return field
        ? field.croppings.find(c => c.croppingId == id)
        : null
}
